import { AlkLogger } from '@alk/core';
import { arrayInclude } from '../../helpers/array-utils';
import {
    assignPredefinedFlagFromTenorRequest,
    excludeAlreadyRequestedTenors,
    keepRequestedTenors,
    resolvedTenorComparer,
} from './tenor-resolve.service.utils';
import {
    filter,
    finalize,
    map,
    pairwise,
    scan,
    shareReplay,
    startWith,
    takeUntil,
} from 'rxjs/operators';
import { inject, injectable } from 'inversify';
import { IResolvedTenor, ITenorRequest } from '../../interfaces';
import {
    ITenorResolveAdapterService,
    TENOR_RESOLVE_ADAPTER_SERVICE,
} from '../message-adapters/tenor-resolve-adapter';
import { ITenorResolveService } from './tenor-resolve.interface';
import { Observable, Subject } from 'rxjs';

@injectable()
export class TenorResolveService implements ITenorResolveService {

    @inject(TENOR_RESOLVE_ADAPTER_SERVICE) private readonly adapter: ITenorResolveAdapterService;

    private resolvedTenors$: Observable<Array<IResolvedTenor>>;
    private resolveRequest$: Subject<Array<ITenorRequest>>;
    private readonly destroyed$ = new Subject<boolean>();

    private started = false;

    resolvedTenorObservable(tenors: Array<ITenorRequest>): Observable<Array<IResolvedTenor>> {
        AlkLogger.debug(`[TenorResolveService#resolvedTenorObservable] Asked to resolve ${ JSON.stringify(tenors) }`);

        if (!this.started){
            this.start();
        }

        this.resolveRequest$.next(tenors);

        return this.rawFeed().pipe(
            keepRequestedTenors(tenors),
        );
    }

    private stop(): void {
        AlkLogger.debug(`[TenorResolveService#stop] service stopped`);

        this.destroyed$.next(true);

        this.resolvedTenors$ = undefined;

        this.started = false;
    }

    private rawFeed(): Observable<Array<IResolvedTenor>>{
        if (!Boolean(this.resolvedTenors$)) {
            this.start();
        }

        return this.resolvedTenors$;
    }

    private start(): void {
        this.resolveRequest$ = new Subject<Array<ITenorRequest>>();

        // NOTE: This observable accumulate the requested tenors to be resolve.
        const accumulatedResolveRequest$ = this.resolveRequest$.pipe(
            scan(arrayInclude<IResolvedTenor>(resolvedTenorComparer), []),
            shareReplay(),
        );

        // NOTE: This observable is an accumulated list of all the resolved
        // tenors ever requested during the session.
        this.resolvedTenors$ = this.adapter.resolvedTenorObservable().pipe(
            takeUntil(this.destroyed$),
            assignPredefinedFlagFromTenorRequest(accumulatedResolveRequest$),
            scan(arrayInclude<IResolvedTenor>(resolvedTenorComparer), []),
            finalize(() => this.stop()),
            shareReplay(),
        );

        // NOTE: This observable is a postpone version of the source observable. It is
        // always late of one (with pairwise and map) in order to ignore the current
        // request. If not, there will never be new requested tenors.
        const postponeAccumulatedResolvedRequest$ = accumulatedResolveRequest$.pipe(
            pairwise(),
            map(([ previous ]) => previous),
            startWith([]),
        );

        // NOTE: This observable extract new requested tenors from the accumulate
        // list of request already send to the adapter (Backend). The goal here is
        // to avoid asking twice the same question.
        this.resolveRequest$.pipe(
            takeUntil(this.destroyed$),
            excludeAlreadyRequestedTenors(postponeAccumulatedResolvedRequest$),
            filter(array => Boolean(array.length)),
        ).subscribe(notYetResolvedTenors => {
            AlkLogger.debug(`[TenorResolveService#foldTenorRequest] about to create ${ ''
            }tenor resolve subscription for ${ JSON.stringify(notYetResolvedTenors) }`);

            this.adapter.create(notYetResolvedTenors);
        });

        this.started = true;
    }
}
